// ─── src/MeetupProposalsView.tsx ──────────────────────────────────────────────
import { useState, useCallback, useMemo } from "react";
import { Download, Search, X, CalendarDays, Clock, CheckCircle, XCircle, UserPlus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useMeetupProposals,
  useMeetupProposalStats,
  useApproveMeetupProposal,
  useRejectMeetupProposal,
  useDeleteMeetupProposal,
  useUpdateMeetupProposal,
  meetupProposalKeys,
} from "./hooks/useMeetupProposals";
import {
  useMeetupEvents,
  useUpdateMeetupEvent,
} from "./hooks/useMeetupEvents";
import { MeetupProposalsTable } from "./components/dashboard/MeetupProposalsTable";
import { EditProposalModal } from "./components/dashboard/EditProposalModal";
import { EditEventModal } from "./components/dashboard/EditEventModal";
import { Pagination }    from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard }      from "./components/ui/StatCard";
import type { MeetupProposal } from "./types/meetupProposal";
import type { MeetupEvent } from "./types/meetupEvent";

const LIMIT = 20;

function exportCsv(rows: MeetupProposal[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    "Host Name", "Host Gender", "Host Governorate", "Host City/Area", "Host Phone", "Host Email",
    "Co-Host Name", "Co-Host Email", "Additional Co-Organizers",
    "Event Title", "Target Audience",
    "Event Format", "Format (other)", "Motivation", "Agenda", "Structure", "Series Details",
    "Speakers", "Date", "Start Time", "Duration (h)", "Venue", "Venue Status",
    "Expected Attendees", "Other Details",
    "Partner Support", "Partnership Details", "Logistics Support",
    "Wants Facebook Event",
    "Commitments Accepted", "Status", "Submitted At",
  ].join(",");
  const body = rows.map((r) =>
    [
      r.hostFullNameAr, r.hostGender ?? "", r.hostGovernorate, r.hostCityArea, r.hostPhone, r.hostEmail,
      r.coHostName ?? "", r.coHostEmail ?? "", r.additionalCoHosts ?? "",
      r.eventTitle ?? "", r.targetAudience ?? "",
      r.eventFormat, r.eventFormatOther ?? "", r.motivation, r.agenda ?? "", r.structure,
      r.seriesDetails ?? "", r.speakers ?? "", r.proposedDate, r.startTime,
      r.durationHours, r.venue, r.venueStatus ?? "",
      r.expectedAttendees, r.otherDetails ?? "",
      r.partnerSupport, r.partnershipDetails, r.logisticsSupport ?? "",
      r.wantsFacebookEvent ? "Yes" : "No",
      `${r.commitments?.length ?? 0}/5`, r.status, r.createdAt,
    ].map(esc).join(","),
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `meetup_proposals_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

interface Props {
  onViewAttendees: (event: MeetupEvent) => void;
}

export default function MeetupProposalsView({ onViewAttendees }: Props) {
  const qc = useQueryClient();
  const [search, setSearch]           = useState("");
  const [governorate, setGovernorate] = useState("");
  const [status, setStatus]           = useState("");
  const [date, setDate]               = useState("");
  const [format, setFormat]           = useState("");
  const [visualPage, setVisualPage]   = useState(1);

  const { data: stats } = useMeetupProposalStats();
  const { data: allData } = useMeetupProposals({ page: 1, limit: 2000 });
  const allRows = allData?.data ?? [];

  // Published events, keyed by the proposal they came from — lets each
  // proposal row show its live capacity/attendee stats once approved.
  const { data: eventsData } = useMeetupEvents({ page: 1, limit: 500 });
  const eventsByProposalId = useMemo(() => {
    const map = new Map<string, MeetupEvent>();
    for (const e of eventsData?.data ?? []) map.set(e.proposalId, e);
    return map;
  }, [eventsData]);

  const { data: tableData, isLoading, isFetching, isError } = useMeetupProposals({
    page: visualPage,
    limit: LIMIT,
    ...(search ? { search } : {}),
    ...(governorate ? { governorate } : {}),
    ...(status ? { status } : {}),
    ...(date ? { date } : {}),
    ...(format ? { format } : {}),
  });

  const { mutate: approveMutate, isPending: isApproving } = useApproveMeetupProposal();
  const { mutate: rejectMutate,  isPending: isRejecting }  = useRejectMeetupProposal();
  const { mutate: deleteRow, isPending: isDeleting } = useDeleteMeetupProposal();
  const { mutateAsync: updateProposal, isPending: isUpdating } = useUpdateMeetupProposal();
  const { mutateAsync: updateEvent, isPending: isUpdatingEvent } = useUpdateMeetupEvent();
  const [editing, setEditing] = useState<MeetupProposal | null>(null);
  const [editingEvent, setEditingEvent] = useState<MeetupEvent | null>(null);

  const pageRows  = tableData?.data ?? [];
  const tableMeta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };
  const governorateOptions = Object.keys(stats?.byGovernorate ?? {}).sort();
  const formatOptions = Object.keys(stats?.byEventFormat ?? {}).sort();
  const hasFilters = !!(search || governorate || status || date || format);
  const clearFilters = () => {
    setSearch(""); setGovernorate(""); setStatus(""); setDate(""); setFormat(""); setVisualPage(1);
  };

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: meetupProposalKeys.all });
  }, [qc]);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Total Proposals" value={stats?.total ?? 0} icon={CalendarDays} accent="bg-brand-green" />
        <StatCard label="Waiting for Approval" value={stats?.pending ?? 0} icon={Clock} accent="bg-brand-teal" />
        <StatCard label="Accepted" value={stats?.accepted ?? 0} icon={CheckCircle} accent="bg-brand-green" />
        <StatCard label="Rejected" value={stats?.rejected ?? 0} icon={XCircle}     accent="bg-brand-teal" />
        <StatCard label="With Co-Host" value={stats?.withCoHost ?? 0} icon={UserPlus} accent="bg-brand-teal" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisualPage(1); }}
            placeholder="Search event name, host, email, phone, venue…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-card border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green transition-colors"
          />
          {search && (
            <button onClick={() => { setSearch(""); setVisualPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
              <X size={14} />
            </button>
          )}
        </div>

        <select
          value={governorate}
          onChange={(e) => { setGovernorate(e.target.value); setVisualPage(1); }}
          className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors"
        >
          <option value="">All Governorates</option>
          {governorateOptions.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>

        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setVisualPage(1); }}
          className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors"
        >
          <option value="">All Statuses</option>
          <option value="pending">Waiting for Approval</option>
          <option value="accepted">Published</option>
          <option value="rejected">Rejected</option>
        </select>

        <select
          value={format}
          onChange={(e) => { setFormat(e.target.value); setVisualPage(1); }}
          className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors"
        >
          <option value="">All Formats</option>
          {formatOptions.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>

        <input
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); setVisualPage(1); }}
          title="Proposals running on this date"
          className="py-2 px-3 text-sm bg-brand-card border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors [color-scheme:dark]"
        />

        {hasFilters && (
          <button onClick={clearFilters} className="btn-ghost text-sm flex items-center gap-1.5">
            <X size={14} /> Clear filters
          </button>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <button onClick={handleRefresh} disabled={isFetching} className="btn-ghost text-sm flex items-center gap-1.5">
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <button onClick={() => exportCsv(allRows)} disabled={allRows.length === 0} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">Failed to load proposals. Check your API key or network.</div>
      ) : (
        <>
          <MeetupProposalsTable
            rows={pageRows}
            eventsByProposalId={eventsByProposalId}
            onDelete={(id) => deleteRow(id)}
            onApprove={(id, imageUrl) => approveMutate({ id, imageUrl })}
            // ^ imageUrl is optional now; the inner table calls with
            // just `id` after a plain confirm().
            onReject={(id) => rejectMutate(id)}
            onEdit={(p) => setEditing(p)}
            onEditEvent={(e) => setEditingEvent(e)}
            onViewAttendees={onViewAttendees}
            isBusy={isApproving || isRejecting || isDeleting || isUpdating}
          />
          <Pagination
            page={tableMeta.page}
            totalPages={tableMeta.totalPages}
            total={tableMeta.total}
            limit={LIMIT}
            onPageChange={setVisualPage}
            isFetching={isFetching}
          />
        </>
      )}

      {editing && (
        <EditProposalModal
          proposal={editing}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            await updateProposal({ id: editing.id, payload });
            setEditing(null);
          }}
          saving={isUpdating}
        />
      )}

      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSave={async (payload) => {
            await updateEvent({ id: editingEvent.id, payload });
            setEditingEvent(null);
          }}
          saving={isUpdatingEvent}
          subtitle="Admin edit — changes go live immediately on the public page and reflect for the catalyst too."
        />
      )}
    </div>
  );
}
